import React, { useState, useEffect } from 'react';
import { TagIcon } from '@heroicons/react/24/outline';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { executeAgent } from '../../services/agentService';
import AgentOutputActions from '../../components/Agents/AgentOutputActions';
import SecurityViolationModal from '../../components/Agents/SecurityViolationModal';
import { validateFields } from '../../utils/securityValidator';
import { formatElapsedTime } from '../../utils/timeUtils';
import { Step } from '../../types';
import { 
  AgentPageLayout, 
  AgentFormSection, 
  AgentFormGroup, 
  AgentLabel, 
  AgentTextarea,
  AgentButton, 
  AgentError, 
  AgentResult,
  AgentDisclaimer
} from '../../components/Agents/AgentPageLayout';

const TargetAudienceDefiner: React.FC = () => {
  const [whoToReach, setWhoToReach] = useState('');
  const [importantValues, setImportantValues] = useState('');
  const [biggestChallenges, setBiggestChallenges] = useState('');
  const [whatTheySeek, setWhatTheySeek] = useState('');
  const [desiredImpact, setDesiredImpact] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [steps, setSteps] = useState<Step[]>([]);
  const [hasStarted, setHasStarted] = useState<boolean>(false);
  const [showSecurityModal, setShowSecurityModal] = useState<boolean>(false);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [elapsedTime, setElapsedTime] = useState<number>(0);

  useEffect(() => {
    if (!isLoading || startTime === null) return;
    const interval = setInterval(() => {
      setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [isLoading, startTime]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!whoToReach.trim()) {
      setError('Por favor, responda pelo menos a primeira pergunta.');
      return;
    }

    // Validar segurança antes de enviar
    if (validateFields(whoToReach, importantValues, biggestChallenges, whatTheySeek, desiredImpact)) {
      setShowSecurityModal(true);
      return;
    }

    setIsLoading(true);
    setError('');
    setResult('');
    setHasStarted(true);
    setStartTime(Date.now());
    setElapsedTime(0);
    setSteps([{ id: '1', title: 'Definindo público-alvo...', status: 'running' }]);

    try {
      const questionsAnswers = [
        { question: 'Quem você quer alcançar?', answer: whoToReach },
        { question: 'Quais são os valores mais importantes dessa pessoa?', answer: importantValues },
        { question: 'Quais são os maiores desafios que essa pessoa enfrenta em sua fé e em sua vida diária?', answer: biggestChallenges },
        { question: 'O que essa pessoa busca em termos de conteúdo, produtos ou serviços?', answer: whatTheySeek },
        { question: 'Qual o impacto que você deseja causar na vida dessa pessoa?', answer: desiredImpact },
      ].filter(qa => qa.answer.trim());

      const prompt = `Com base nas seguintes respostas, defina o público-alvo de forma precisa e direcione a comunicação conforme o contexto ministerial:\n\n${questionsAnswers.map((qa, idx) => `${idx + 1}. ${qa.question}\n${qa.answer}`).join('\n\n')}`;

      const parameters = {
        prompt,
        respostas: questionsAnswers
      };

      const data = await executeAgent('definir-publico-alvo', parameters);
      setResult(data.text);
      setSteps([{ id: '1', title: 'Público-alvo definido', status: 'success' }]);
    } catch (err: any) {
      const errorMessage = err.message || 'Erro ao executar o agente';
      setError(`Erro: ${errorMessage}`);
      setSteps([{ id: '1', title: 'Erro ao definir público-alvo', status: 'error', error: errorMessage }]);
      console.error('Erro ao executar agente:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setWhoToReach('');
    setImportantValues('');
    setBiggestChallenges('');
    setWhatTheySeek('');
    setDesiredImpact('');
    setResult('');
    setError('');
    setSteps([]);
    setHasStarted(false);
  };

  return (
    <AgentPageLayout
      icon={<TagIcon className="w-6 h-6 text-secondary" />}
      title="Definir Público-alvo"
      subtitle="Defina o público de forma precisa e direcione a comunicação conforme o contexto ministerial."
    >
      <AgentFormSection>
        <form onSubmit={handleSubmit} className="space-y-6">
          <h2 className="text-xl font-bold text-foreground mb-4">
            Responda Algumas Perguntas
          </h2>

          <AgentFormGroup>
            <AgentLabel htmlFor="whoToReach">Quem você quer alcançar?</AgentLabel>
            <AgentTextarea
              id="whoToReach"
              value={whoToReach}
              onChange={(e) => setWhoToReach(e.target.value)}
              placeholder="Responda com o máximo de detalhes possível.."
              disabled={isLoading}
              rows={4}
            />
          </AgentFormGroup>

          <AgentFormGroup>
            <AgentLabel htmlFor="importantValues">Quais são os valores mais importantes dessa pessoa?</AgentLabel>
            <AgentTextarea
              id="importantValues"
              value={importantValues}
              onChange={(e) => setImportantValues(e.target.value)}
              placeholder="Responda com o máximo de detalhes possível.."
              disabled={isLoading}
              rows={4}
            />
          </AgentFormGroup>

          <AgentFormGroup>
            <AgentLabel htmlFor="biggestChallenges">Quais são os maiores desafios que essa pessoa enfrenta em sua fé e em sua vida diária?</AgentLabel>
            <AgentTextarea
              id="biggestChallenges"
              value={biggestChallenges}
              onChange={(e) => setBiggestChallenges(e.target.value)}
              placeholder="Responda com o máximo de detalhes possível.."
              disabled={isLoading}
              rows={4}
            />
          </AgentFormGroup>

          <AgentFormGroup>
            <AgentLabel htmlFor="whatTheySeek">O que essa pessoa busca em termos de conteúdo, produtos ou serviços?</AgentLabel>
            <AgentTextarea
              id="whatTheySeek"
              value={whatTheySeek}
              onChange={(e) => setWhatTheySeek(e.target.value)}
              placeholder="Responda com o máximo de detalhes possível.."
              disabled={isLoading}
              rows={4}
            />
          </AgentFormGroup>

          <AgentFormGroup>
            <AgentLabel htmlFor="desiredImpact">Qual o impacto que você deseja causar na vida dessa pessoa?</AgentLabel>
            <AgentTextarea
              id="desiredImpact"
              value={desiredImpact}
              onChange={(e) => setDesiredImpact(e.target.value)}
              placeholder="Responda com o máximo de detalhes possível.."
              disabled={isLoading}
              rows={4}
            />
          </AgentFormGroup>

          <div className="flex justify-end">
            <AgentButton type="submit" disabled={isLoading}>
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Definindo Público-alvo...</span>
                </>
              ) : (
                <>
                  <TagIcon className="w-5 h-5" />
                  <span>Definir Público-alvo</span>
                </>
              )}
            </AgentButton>
          </div>
        </form>

        {error && <AgentError message={error} />}

        {hasStarted && (
          <section className="mt-8">
            <div className="bg-card border border-border rounded-lg p-6">
              {isLoading && (
                <div className="flex flex-col items-center justify-center gap-4 py-8">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">😇</span>
                    <h3 className="text-lg font-semibold text-foreground">
                      Enquanto trabalhamos, eleve seu pensamento e descanse na presença de Deus.
                    </h3>
                  </div>
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary"></div>
                  {steps.length > 0 && steps[0]?.title && (
                    <p className="text-sm text-muted-foreground">{steps[0].title}</p>
                  )}
                  {elapsedTime > 0 && (
                    <p className="text-xs text-muted-foreground">
                      Tempo de execução: {formatElapsedTime(elapsedTime)}
                    </p>
                  )}
                </div>
              )}
            </div>
          </section>
        )}

        {result && (
          <AgentResult title="Público-alvo Definido">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {result}
            </ReactMarkdown>
            <AgentDisclaimer />
            <div className="mt-6 flex items-center justify-end gap-3">
              <AgentOutputActions
                content={result}
                agentName="Definir Público-alvo"
                agentCode="definir-publico-alvo"
                reference={whoToReach || 'Público-alvo'}
              />
              <AgentButton
                type="button"
                onClick={handleReset}
                variant="secondary"
                aria-label="Reiniciar"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-5 h-5" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
                </svg>
                <span>Reiniciar</span>
              </AgentButton>
            </div>
          </AgentResult>
        )}
      </AgentFormSection>

      <SecurityViolationModal
        open={showSecurityModal}
        onClose={() => setShowSecurityModal(false)}
        onRestart={handleReset}
      />
    </AgentPageLayout>
  );
};

export default TargetAudienceDefiner;
